import React from 'react';
import { HeaderContent } from './HeaderContent';
import { flexRender, HeaderGroup } from '@tanstack/react-table';
import { SELECT_WIDTH, columnSizeVar } from './constants';
import { Input } from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import messages from 'src/messages';
import { useTableContext } from './TableContext';
import { queryClient } from 'src/query-client';
import { toast } from 'react-toastify';
import {
  getPinnedColumnIds,
  moveColumnInOrder,
  persistVisibleColumnOrder,
  sameColumnOrder,
} from './columnOrderUtils';

type DropSide = 'before' | 'after';

export function HeaderCell<T>({
  headerGroup,
  isResizingColumn,
}: {
  headerGroup: HeaderGroup<T>;
  isResizingColumn: string | boolean;
}) {
  const { formatMessage } = useIntl();
  const {
    showSelectionColumn,
    columnOrder,
    setColumnOrder,
    tableId,
    defaultPinnedColumn,
    table,
    queryKey,
    canReorderColumns,
  } = useTableContext();

  const pinnedIds = React.useMemo(
    () => getPinnedColumnIds(defaultPinnedColumn),
    [defaultPinnedColumn]
  );

  const [draggingId, setDraggingId] = React.useState<string | null>(null);
  const [dropTarget, setDropTarget] = React.useState<{
    id: string;
    side: DropSide;
  } | null>(null);

  const onDropColumn = React.useCallback(
    async (draggedId: string, targetId: string, side: DropSide) => {
      const nextOrder = moveColumnInOrder(
        columnOrder,
        draggedId,
        targetId,
        side,
        pinnedIds
      );
      if (sameColumnOrder(nextOrder, columnOrder)) {
        return;
      }

      setColumnOrder(nextOrder);

      try {
        const orderedVisibleIds = await persistVisibleColumnOrder(
          table,
          nextOrder,
          tableId
        );
        // Keep the cached visible list in step so a refetch doesn't
        // restore the previous order
        queryClient.setQueryData(queryKey, (prev: any) =>
          prev ? { ...prev, visible: orderedVisibleIds } : prev
        );
      } catch (error) {
        console.error('Failed to save column order:', error);
        toast.error('Failed to save column order');
        setColumnOrder(columnOrder);
      }
    },
    [columnOrder, pinnedIds, queryKey, setColumnOrder, table, tableId]
  );

  return (
    <tr>
      {headerGroup.headers.map((header) => {
        const id = header.column.columnDef.id!;
        const isSticky = id === 'select' || id === defaultPinnedColumn;
        const isPinned = pinnedIds.includes(id);
        const canDrag = canReorderColumns && !isPinned;
        const meta = header.column.columnDef.meta;
        const isDropBefore =
          dropTarget?.id === id && dropTarget.side === 'before';
        const isDropAfter =
          dropTarget?.id === id && dropTarget.side === 'after';

        const thStyle: React.CSSProperties = {
          width:
            id === 'select'
              ? '40px'
              : meta?.flex
                ? 'auto'
                : `calc(var(${columnSizeVar('header', header.id)}) * 1px)`,
          left:
            isSticky && showSelectionColumn
              ? id === 'select'
                ? 0
                : `${SELECT_WIDTH}px`
              : undefined,
          cursor: header.column.getCanSort() ? 'pointer' : 'default',
          // prevent mouse release triggering a sort when finishing a resize
          pointerEvents: isResizingColumn ? 'none' : 'auto',
          opacity: draggingId === id ? 0.5 : 1,
        };

        const classNames = [
          isSticky ? 'lp-table-sticky' : '',
          isDropBefore ? 'lp-table-col-drop-before' : '',
          isDropAfter ? 'lp-table-col-drop-after' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <th
            key={header.id}
            style={thStyle}
            className={classNames || undefined}
            onDragOver={(e) => {
              if (!draggingId || isPinned || draggingId === id) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';

              const rect = e.currentTarget.getBoundingClientRect();
              const side: DropSide =
                e.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
              if (dropTarget?.id !== id || dropTarget?.side !== side) {
                setDropTarget({ id, side });
              }
            }}
            onDragLeave={(e) => {
              // Only clear when leaving the th itself, not when entering a child
              if (
                dropTarget?.id === id &&
                !e.currentTarget.contains(e.relatedTarget as Node)
              ) {
                setDropTarget(null);
              }
            }}
            onDrop={(e) => {
              // Ignore drops that aren't a column drag, e.g. text dragged
              // into a filter input
              if (!draggingId) return;
              e.preventDefault();
              const draggedId =
                e.dataTransfer.getData('text/plain') || draggingId;
              const side = dropTarget?.id === id ? dropTarget.side : null;
              setDraggingId(null);
              setDropTarget(null);
              if (draggedId && side && !isPinned) {
                void onDropColumn(draggedId, id, side);
              }
            }}
            onClick={(e) => {
              // Allow sorting only when clicking the th or the header content div
              // i.e. not the resizer or the filter input
              if (
                e.target === e.currentTarget ||
                (e.target as HTMLElement).closest('.lp-table-header-content')
              ) {
                header.column.getToggleSortingHandler()?.(e);
              }
            }}
          >
            <div
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: id === 'select' ? 'center' : 'space-between',
              }}
            >
              <HeaderContent
                id={id}
                sortString={header.column.getIsSorted() as string | null}
                draggable={canDrag}
                style={{
                  cursor:
                    draggingId === id
                      ? 'grabbing'
                      : canDrag
                        ? 'grab'
                        : undefined,
                }}
                onDragStart={(e) => {
                  if (!canDrag) return;
                  setDraggingId(id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', id);
                }}
                onDragEnd={() => {
                  setDraggingId(null);
                  setDropTarget(null);
                }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </HeaderContent>

              {header.column.getCanFilter() && (
                <div style={{ cursor: 'text' }}>
                  {meta?.customFilter ? (
                    meta.customFilter(header.column)
                  ) : (
                    <Input
                      value={(header.column.getFilterValue() as string) ?? ''}
                      onChange={(e) =>
                        header.column.setFilterValue(e.target.value)
                      }
                      placeholder={formatMessage(messages.filter)}
                      className={
                        (header.column.getFilterValue() as string)
                          ? 'lp-table-filter filtered'
                          : 'lp-table-filter unfiltered'
                      }
                      transparent
                      icon={{
                        name: 'cancel',
                        onClick: () => header.column.setFilterValue(''),
                        link: true,
                        title: formatMessage(messages.clearFilter),
                      }}
                    />
                  )}
                </div>
              )}

              {header.column.getCanResize() && (
                <div
                  onMouseDown={header.getResizeHandler()}
                  onTouchStart={header.getResizeHandler()}
                  className={`resizer ${
                    header.column.getIsResizing() ? 'isResizing' : ''
                  }`}
                />
              )}
            </div>
          </th>
        );
      })}
    </tr>
  );
}
